import { useEffect, useState } from "react";
import { UserPlus, X, Search, CheckCircle2, RotateCcw, Users } from "lucide-react";
import { LeadSearch } from "./LeadSearch";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useTaxonomy } from "../lib/useTaxonomy";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-1.5";

// Screen A: initial two-option chooser
const Chooser = ({ onExisting, onLead, onTrial, onClose }) => (
  <div className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-md">
    <div className="flex justify-between items-start mb-5">
      <h2 className="font-heading text-xl font-black">Add Player to This Session</h2>
      <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="add-player-close"><X size={20} /></button>
    </div>
    <p className="text-white/50 text-sm mb-6">Who's joining today's session?</p>
    <div className="space-y-3">
      <button onClick={onExisting} className="w-full flex items-center gap-4 text-left border border-white/15 bg-[#050505] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 p-4 transition-colors duration-200" data-testid="add-choose-existing">
        <RotateCcw size={20} className="text-[#D4AF37] shrink-0" />
        <div>
          <p className="font-semibold text-sm">Existing / Inactive Player</p>
          <p className="text-white/40 text-xs mt-0.5">Search everyone on record. Returning kids don't get a new ID.</p>
        </div>
      </button>
      <button onClick={onLead} className="w-full flex items-center gap-4 text-left border border-white/15 bg-[#050505] hover:border-blue-400 hover:bg-blue-400/5 p-4 transition-colors duration-200" data-testid="add-choose-lead">
        <Users size={20} className="text-blue-300 shrink-0" />
        <div>
          <p className="font-semibold text-sm">Lead from Leads Tab</p>
          <p className="text-white/40 text-xs mt-0.5">A lead you already have on record is attending a trial today.</p>
        </div>
      </button>
      <button onClick={onTrial} className="w-full flex items-center gap-4 text-left border border-white/15 bg-[#050505] hover:border-blue-400 hover:bg-blue-400/5 p-4 transition-colors duration-200" data-testid="add-choose-trial">
        <UserPlus size={20} className="text-blue-300 shrink-0" />
        <div>
          <p className="font-semibold text-sm">New Trial Player</p>
          <p className="text-white/40 text-xs mt-0.5">First-time walk-in. They'll be added to Leads for follow-up.</p>
        </div>
      </button>
    </div>
  </div>
);

// Screen B: search existing/inactive players
const ExistingSearch = ({ onPick, onBack, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/admin/players-search", { params: { q, include_inactive: true } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-2xl max-h-[80vh] flex flex-col">
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Existing Player</p>
          <h2 className="font-heading text-xl font-black mt-1">Search the Roster</h2>
        </div>
        <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
      </div>
      <div className="relative mb-4">
        <Search size={16} className="text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input autoFocus placeholder="Search name or ZFC ID…" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls + " w-full pl-10"} data-testid="existing-search" />
      </div>
      <div className="flex-1 overflow-y-auto space-y-1.5 min-h-[240px]" data-testid="existing-results">
        {loading && <p className="text-white/40 text-xs">Searching…</p>}
        {!loading && q.length > 0 && results.length === 0 && <p className="text-white/40 text-sm text-center py-6">No players match "{q}".</p>}
        {!loading && q.length === 0 && <p className="text-white/40 text-sm text-center py-6">Start typing to find any player — including inactive ones.</p>}
        {results.map((p) => {
          const isInactive = (p.status || "Active") !== "Active";
          return (
            <button key={p.player_id} onClick={() => onPick(p)}
              className="w-full text-left border border-white/10 bg-[#050505] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 p-3 transition-colors duration-200"
              data-testid={`existing-option-${p.player_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{p.name}</p>
                  <p className="text-white/40 text-xs mt-0.5">{p.player_id} · {p.centre || "No centre"} · {p.batch || (p.batches && p.batches[0]) || "No batch"}</p>
                  {p.last_attendance && <p className="text-white/30 text-[11px] mt-0.5">Last attended: {p.last_attendance}</p>}
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 ${isInactive ? "bg-red-500/15 text-red-300 border border-red-400/30" : "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"}`}>
                  {isInactive ? p.status || "Inactive" : "Active"}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      <button onClick={onBack} className="mt-4 text-[11px] uppercase tracking-widest text-white/40 hover:text-[#D4AF37] self-start" data-testid="existing-back">← Back</button>
    </div>
  );
};

// Screen C: chosen player — pick "Attend only" or "Reactivate"
const PickAction = ({ player, batch, onAttendOnly, onReactivate, onBack, onClose, busy }) => {
  const isInactive = (player.status || "Active") !== "Active";
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-md">
      <div className="flex justify-between items-start mb-5">
        <h2 className="font-heading text-xl font-black">{player.name}</h2>
        <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
      </div>
      <div className="border border-white/10 bg-[#050505] p-3 mb-5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[#D4AF37] text-xs font-semibold">{player.player_id}</p>
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${isInactive ? "bg-red-500/15 text-red-300" : "bg-[#D4AF37]/15 text-[#D4AF37]"}`}>{isInactive ? player.status || "Inactive" : "Active"}</span>
        </div>
        <p className="text-white/60 text-xs">Previous centre: <span className="text-white/90">{player.centre || "—"}</span></p>
        <p className="text-white/60 text-xs">Previous batch: <span className="text-white/90">{player.batch || (player.batches && player.batches.join(", ")) || "—"}</span></p>
        {player.last_attendance && <p className="text-white/60 text-xs">Last attended: <span className="text-white/90">{player.last_attendance}</span></p>}
      </div>
      <div className="space-y-3">
        <button onClick={onAttendOnly} disabled={busy}
          className="w-full flex items-center gap-3 text-left border border-white/15 bg-[#050505] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 p-4 transition-colors duration-200 disabled:opacity-40"
          data-testid="pick-attend-only">
          <CheckCircle2 size={18} className="text-[#D4AF37] shrink-0" />
          <div>
            <p className="font-semibold text-sm">Attend This Session Only</p>
            <p className="text-white/40 text-xs mt-0.5">Mark P for {batch} today. No status change, no auto-enrolment, no dues.</p>
          </div>
        </button>
        {isInactive && (
          <button onClick={onReactivate} disabled={busy}
            className="w-full flex items-center gap-3 text-left border border-white/15 bg-[#050505] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 p-4 transition-colors duration-200 disabled:opacity-40"
            data-testid="pick-reactivate">
            <RotateCcw size={18} className="text-[#D4AF37] shrink-0" />
            <div>
              <p className="font-semibold text-sm">Reactivate Player</p>
              <p className="text-white/40 text-xs mt-0.5">Formally re-enrol. Set centre, batch, fee plan and effective date. No registration fee.</p>
            </div>
          </button>
        )}
      </div>
      <button onClick={onBack} className="mt-4 text-[11px] uppercase tracking-widest text-white/40 hover:text-[#D4AF37]">← Back to search</button>
    </div>
  );
};

// Screen D: reactivation form
const ReactivateForm = ({ player, defaultBatch, defaultCentre, sessionDate, onSaved, onBack, onClose }) => {
  const { centreNames, batches: allBatches } = useTaxonomy();
  const [f, setF] = useState({
    centre: defaultCentre || player.centre || "",
    batch: defaultBatch || player.batch || (player.batches && player.batches[0]) || "",
    effective_from: sessionDate || new Date().toISOString().slice(0, 10),
    group_fee: "",
    transport_fee: "",
    pt_fee: "",
    opening_dues: "",
    mark_this_session: true,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.centre || !f.batch || !f.effective_from) {
      toast.error("Centre, batch and effective date are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/players/${player.player_id}/reactivate`, {
        centre: f.centre,
        batch: f.batch,
        effective_from: f.effective_from,
        group_fee: f.group_fee !== "" ? Number(f.group_fee) : 0,
        transport_fee: f.transport_fee !== "" ? Number(f.transport_fee) : 0,
        pt_fee: f.pt_fee !== "" ? Number(f.pt_fee) : 0,
        opening_dues: f.opening_dues !== "" ? Number(f.opening_dues) : 0,
        session_date: f.mark_this_session ? sessionDate : null,
      });
      toast.success(`${player.name} reactivated`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  // Batches filtered by chosen centre
  const batchOptions = allBatches.filter((b) => !f.centre || b.centre === f.centre).map((b) => b.name);

  return (
    <form onSubmit={submit} className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-lg max-h-[85vh] overflow-y-auto">
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Reactivate</p>
          <h2 className="font-heading text-xl font-black mt-1">{player.name}</h2>
          <p className="text-white/40 text-xs mt-1">{player.player_id} · previously {player.centre || "—"} · {player.batch || "—"}</p>
        </div>
        <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
      </div>
      <p className="text-white/50 text-xs mb-5 border-l-2 border-[#D4AF37]/40 pl-3">Existing player — registration fee will NOT be charged automatically.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Centre *</label>
          <select required value={f.centre} onChange={(e) => setF({ ...f, centre: e.target.value, batch: "" })} className={inputCls + " w-full"} data-testid="react-centre">
            <option value="">—</option>
            {centreNames.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Batch *</label>
          <select required value={f.batch} onChange={(e) => setF({ ...f, batch: e.target.value })} className={inputCls + " w-full"} data-testid="react-batch">
            <option value="">—</option>
            {batchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Effective From *</label>
          <input required type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} className={inputCls + " w-full"} data-testid="react-effective" />
        </div>
        <div>
          <label className={labelCls}>Group Fee (₹)</label>
          <input type="number" min="0" value={f.group_fee} onChange={(e) => setF({ ...f, group_fee: e.target.value })} className={inputCls + " w-full"} data-testid="react-group" />
        </div>
        <div>
          <label className={labelCls}>Transport (₹)</label>
          <input type="number" min="0" value={f.transport_fee} onChange={(e) => setF({ ...f, transport_fee: e.target.value })} className={inputCls + " w-full"} data-testid="react-transport" />
        </div>
        <div>
          <label className={labelCls}>PT Fee (₹)</label>
          <input type="number" min="0" value={f.pt_fee} onChange={(e) => setF({ ...f, pt_fee: e.target.value })} className={inputCls + " w-full"} data-testid="react-pt" />
        </div>
        <div>
          <label className={labelCls}>Current Dues (₹)</label>
          <input type="number" min="0" value={f.opening_dues} onChange={(e) => setF({ ...f, opening_dues: e.target.value })} className={inputCls + " w-full"} data-testid="react-dues" />
        </div>
      </div>
      {sessionDate && (
        <label className="flex items-center gap-2 mt-4 text-sm text-white/70 cursor-pointer" data-testid="react-mark-today">
          <input type="checkbox" checked={f.mark_this_session} onChange={(e) => setF({ ...f, mark_this_session: e.target.checked })} className="accent-[#D4AF37]" />
          Also mark them Present for today's session ({sessionDate})
        </label>
      )}
      <div className="flex gap-3 mt-6 justify-end">
        <button type="button" onClick={onBack} disabled={busy} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-5 py-2.5 hover:border-[#D4AF37] disabled:opacity-40">Back</button>
        <button type="submit" disabled={busy} className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-2.5 hover:bg-[#F3D059] disabled:opacity-40" data-testid="react-save">
          {busy ? "Saving…" : "Reactivate Player"}
        </button>
      </div>
    </form>
  );
};

export const AddPlayerToSession = ({ batch, centre, date, coach, onClose, onExistingPicked, onTrialPicked, onReactivated }) => {
  const [step, setStep] = useState("choose"); // choose | existing | lead | pick | reactivate
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const markLead = async (lead) => {
    setBusy(true);
    try {
      await api.post("/admin/attendance", { date, batch, centre, coach: coach || null, marks: [{ player_id: lead.id, mark: "T" }] });
      toast.success(`${lead.name} marked as trial for this session`);
      onExistingPicked(lead);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const attendOnly = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      // Save one-off P mark against this player's ID for this batch/date via existing attendance endpoint
      await api.post("/admin/attendance", {
        date, batch, centre, coach: coach || null,
        marks: [{ player_id: selected.player_id, mark: "P" }],
      });
      toast.success(`${selected.name} marked P for this session`);
      onExistingPicked(selected);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto" onClick={onClose} data-testid="add-player-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full flex justify-center">
        {step === "choose" && (
          <Chooser
            onExisting={() => setStep("existing")}
            onLead={() => setStep("lead")}
            onTrial={() => { onTrialPicked(); }}
            onClose={onClose}
          />
        )}
        {step === "lead" && <LeadSearch busy={busy} onPick={markLead} onBack={() => setStep("choose")} onClose={onClose} />}
        {step === "existing" && (
          <ExistingSearch
            onPick={(p) => { setSelected(p); setStep("pick"); }}
            onBack={() => setStep("choose")}
            onClose={onClose}
          />
        )}
        {step === "pick" && selected && (
          <PickAction
            player={selected}
            batch={batch}
            busy={busy}
            onAttendOnly={attendOnly}
            onReactivate={() => setStep("reactivate")}
            onBack={() => { setSelected(null); setStep("existing"); }}
            onClose={onClose}
          />
        )}
        {step === "reactivate" && selected && (
          <ReactivateForm
            player={selected}
            defaultBatch={batch}
            defaultCentre={centre}
            sessionDate={date}
            onSaved={() => onReactivated(selected)}
            onBack={() => setStep("pick")}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
};
